import React from 'react';
import { ClipboardCheck, Home, Lightbulb, MessageSquare, SlidersHorizontal } from 'lucide-react';
import { AppPhase } from '../../../types';
import { isAppShellPhase } from '../../../lib/appShellMode';

/**
 * App-level bottom navigation for the new app shell (Phase 2A).
 *
 * Brand-new implementation. It deliberately does not reuse any legacy shell or
 * tab-bar component, and uses fresh Tailwind utilities only (no legacy nav CSS
 * classes or height tokens) so the old shell stays fully retired.
 *
 * Visible on the five app-shell phases. By default the component self-guards
 * (returns null for any non-app-shell phase, e.g. legacy dormant phases) so it
 * can never leak into screens that should not have it. App.tsx owns the full
 * visibility decision via `shouldShowAppBottomNav` and opts the nav into the
 * non-camera grading workflow screens with {@link AppBottomNavProps.forceVisible}.
 */
export interface AppBottomNavProps {
  phase: AppPhase;
  /** Navigate to an app-shell phase (drives phase + hash via the shell helper). */
  onNavigate: (phase: AppPhase) => void;
  /**
   * Render even when `phase` is not an app-shell phase (e.g. during the grading
   * workflow on non-camera screens). The parent (App.tsx) decides this via
   * `shouldShowAppBottomNav`. No tab is marked active in that case. Defaults to
   * false, preserving the original "five app-shell phases only" behavior.
   */
  forceVisible?: boolean;
}

interface NavTab {
  phase: AppPhase;
  label: string;
  Icon: React.ComponentType<{ className?: string; strokeWidth?: number; 'aria-hidden'?: boolean }>;
}

const TABS: ReadonlyArray<NavTab> = [
  { phase: AppPhase.HOME, label: 'Home', Icon: Home },
  { phase: AppPhase.GRADE_HOME, label: 'Grade', Icon: ClipboardCheck },
  { phase: AppPhase.PLAN_HOME, label: 'Plan', Icon: Lightbulb },
  { phase: AppPhase.COMMUNICATE_HOME, label: 'Communicate', Icon: MessageSquare },
  { phase: AppPhase.MANAGE_HOME, label: 'Manage', Icon: SlidersHorizontal },
];

export const AppBottomNav: React.FC<AppBottomNavProps> = ({
  phase,
  onNavigate,
  forceVisible = false,
}) => {
  // Guard: render on app-shell phases, or wherever the parent explicitly opts
  // in via `forceVisible` (the non-camera grading workflow screens). Stays out
  // of legacy/dormant phases by default.
  if (!isAppShellPhase(phase) && !forceVisible) return null;

  // Privacy 6B: the Privacy Dashboard is a Manage sub-screen, so highlight the
  // Manage tab while it is open.
  const effectivePhase =
    phase === AppPhase.PRIVACY_DASHBOARD || phase === AppPhase.COMPLIANCE_STATUS
      ? AppPhase.MANAGE_HOME
      : phase;

  return (
    <nav
      aria-label="Main navigation"
      data-dg-app-bottom-nav="true"
      className="fixed inset-x-0 bottom-0 z-40 border-t border-dg-border-subtle bg-dg-bg-surface/95 backdrop-blur-md pb-[env(safe-area-inset-bottom)] dark:border-slate-700 dark:bg-slate-900/95"
    >
      <ul className="mx-auto flex w-full max-w-2xl items-stretch justify-between">
        {TABS.map(({ phase: tabPhase, label, Icon }) => {
          const active = effectivePhase === tabPhase;
          return (
            <li key={label} className="flex-1">
              <button
                type="button"
                onClick={() => onNavigate(tabPhase)}
                aria-current={active ? 'page' : undefined}
                aria-label={label}
                className={`flex min-h-[44px] w-full flex-col items-center justify-center gap-0.5 px-1 py-1.5 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-dg-primary-500 ${
                  active
                    ? 'text-dg-primary-600 dark:text-violet-300'
                    : 'text-dg-fg-muted hover:text-dg-fg-secondary dark:text-slate-400'
                }`}
              >
                <Icon className="h-5 w-5 shrink-0" strokeWidth={active ? 2.5 : 2} aria-hidden />
                <span className="block w-full truncate text-center text-[11px] font-semibold leading-tight sm:text-[12px]">
                  {label}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </nav>
  );
};

export default AppBottomNav;
